import { PrintReport } from '@/components/common/PrintReport';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { PRINT_CELL_CLASS, PRINT_TABLE_CLASS } from '@/styles/printTable';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type { MpRegionProjectReport, MpRegionReport } from '../types/marketPromotion.types';

const REPORT_TITLE = 'Region-Wise Market Promotion';

const TEXT_CELL_CLASS = cn(PRINT_CELL_CLASS, 'text-left');
const AMOUNT_CELL_CLASS = cn(PRINT_CELL_CLASS, 'text-right tabular-nums');
const TOTAL_LABEL_CELL_CLASS = cn(PRINT_CELL_CLASS, 'text-right');

interface PrintAmountRow {
  key: number;
  cells: string[];
  amount: number;
}

interface PrintAmountTableProps {
  caption: string;
  headers: string[];
  rows: PrintAmountRow[];
  grandTotal: number;
}

function PrintAmountTable({ caption, headers, rows, grandTotal }: PrintAmountTableProps) {
  const labelColumnCount = headers.length + 1;
  return (
    <table className={PRINT_TABLE_CLASS}>
      <caption className="pb-1 text-left text-xs font-bold">{caption}</caption>
      <thead>
        <tr>
          <th className={cn(TEXT_CELL_CLASS, 'font-bold')}>SL</th>
          {headers.map((header) => (
            <th key={header} className={cn(TEXT_CELL_CLASS, 'font-bold')}>
              {header}
            </th>
          ))}
          <th className={cn(AMOUNT_CELL_CLASS, 'font-bold')}>Distributed (Tk)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.key}>
            <td className={TEXT_CELL_CLASS}>{index + 1}</td>
            {row.cells.map((cell, cellIndex) => (
              <td key={headers[cellIndex]} className={TEXT_CELL_CLASS}>
                {cell}
              </td>
            ))}
            <td className={AMOUNT_CELL_CLASS}>{formatNumber(row.amount)}</td>
          </tr>
        ))}
        <tr className="font-bold">
          <td colSpan={labelColumnCount} className={TOTAL_LABEL_CELL_CLASS}>
            Grand Total
          </td>
          <td className={AMOUNT_CELL_CLASS}>{formatNumber(grandTotal)}</td>
        </tr>
      </tbody>
    </table>
  );
}

interface MpRegionPrintReportProps {
  regions: MpRegionReport | undefined;
  projects: MpRegionProjectReport | undefined;
  dateFrom: string;
  dateTo: string;
}

export function MpRegionPrintReport({
  regions,
  projects,
  dateFrom,
  dateTo,
}: MpRegionPrintReportProps) {
  const regionRows: PrintAmountRow[] =
    regions?.items.map((region) => ({
      key: region.regionId,
      cells: [
        region.sideRegion ?? EMPTY_VALUE,
        region.regionName,
        region.rsmName,
        region.smName ?? EMPTY_VALUE,
      ],
      amount: region.distributedAmount,
    })) ?? [];
  const projectRows: PrintAmountRow[] =
    projects?.items.map((project) => ({
      key: project.promoItemId,
      cells: [project.itemCode ?? EMPTY_VALUE, project.itemName],
      amount: project.distributedAmount,
    })) ?? [];

  return (
    <PrintReport
      title={REPORT_TITLE}
      details={[
        { label: 'From Date', value: formatDisplayDate(dateFrom) },
        { label: 'To Date', value: formatDisplayDate(dateTo) },
      ]}
    >
      <PrintAmountTable
        caption="Region Wise"
        headers={['Side', 'Region', 'Responsible Employee', 'Reporting To']}
        rows={regionRows}
        grandTotal={regions?.totals.distributedAmount ?? 0}
      />
      <PrintAmountTable
        caption="Project Wise"
        headers={['Project Code', 'Donation Project']}
        rows={projectRows}
        grandTotal={projects?.totals.distributedAmount ?? 0}
      />
    </PrintReport>
  );
}
